import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import TaskUpdateForm from '../../components/tasks/TaskUpdateForm';
import taskService from '../../services/taskService';
import { 
  FaClipboardList, FaArrowLeft, FaPencilAlt, FaTrashAlt, 
  FaCalendarCheck, FaUserAlt, FaUserCog, FaClock,
  FaCheckCircle, FaExclamationTriangle, FaInfoCircle, FaProjectDiagram 
} from 'react-icons/fa';
// Import custom CSS
import "../../styles/taskManagement.css";

const TaskDetailPage = () => {
  const { id } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  useEffect(() => {
    const fetchTask = async () => {
      try {
        setLoading(true);
        const response = await taskService.getTaskById(id);
        setTask(response?.data);
        setLoading(false);
       } catch (err) {
        console.error('Error fetching task details:', err);
        setError('Failed to load task details. Please try again later.');
        setLoading(false);
      }
    };
    fetchTask();
    // Ensure linter recognizes currentUser is being used
    const isAdmin = currentUser && currentUser.role === 'admin';
    const isManager = currentUser && currentUser.role === 'manager';
    const isAdminOrManager = isAdmin || isManager;
  }, [id, currentUser]);

  const handleDelete = async () => {
    try {
      // Note: Need to add deleteTask method to taskService
      await taskService.deleteTask(id);
      navigate('/tasks');
    } catch (err) {
      console.error('Error deleting task:', err);
      setError('Failed to delete task. Please try again later.');
    }
  };

  const handleStatusUpdate = () => {
    // Refresh task data after status update
    const fetchUpdatedTask = async () => {
      try {
        const response = await taskService.getTaskById(id);
        setTask(response?.data);
      } catch (err) {
        console.error('Error refreshing task details:', err);
      }
    };

    fetchUpdatedTask();
  };

  if (loading) {
    return (
      <div className="task-container">
        <div className="task-content-wrapper">
          <div className="loading-container">
            <div className="spinner"></div>
            <p className="loading-text">Loading task details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="task-container">
        <div className="task-content-wrapper">
          <div className="error-container">
            <div className="error-content">
              <FaExclamationTriangle className="error-icon" />
              <p className="error-message">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!task) {
    return (
      <div className="task-container">
        <div className="task-content-wrapper">
          <div className="empty-state-container warning">
            <div className="empty-state-content">
              <FaExclamationTriangle className="empty-state-icon warning" />
              <p className="empty-state-message warning">Task not found</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'pending':
        return 'status-badge status-badge-pending pulse-animation';
      case 'in_progress':
        return 'status-badge status-badge-in-progress glow-animation';
      case 'completed':
        return 'status-badge status-badge-completed bounce-animation';
      default:
        return 'status-badge';
    }
  };
  
  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <FaExclamationTriangle className="status-icon pending-icon" />;
      case 'in_progress':
        return <FaClipboardList className="status-icon progress-icon" />;
      case 'completed':
        return <FaCheckCircle className="status-icon completed-icon" />;
      default:
        return <FaInfoCircle className="status-icon" />;
    }
  };
  
  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return 'PENDING';
      case 'in_progress':
        return 'IN PROGRESS';
      case 'completed':
        return 'COMPLETED';
      default:
        return status.toUpperCase();
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="task-container task-detail-page fade-in-animation">
      <div className="task-content-wrapper max-width-1200">
        <div className="back-link-container slide-in-left">
          <Link to="/tasks" className="back-link">
            <FaArrowLeft className="back-link-icon" />
            <span>Back to Tasks</span>
          </Link>
        </div>

        <div className="task-detail-card">
          <div className="task-detail-card-pattern"></div>
        <div className="task-detail-header">
          <div className="task-detail-header-content">
            <h1 className="task-detail-title">{task.title}</h1>
            <div className="task-id">#{task.id}</div>
          </div>
          <span className={getStatusBadgeClass(task.status)}>
            {getStatusIcon(task.status)}
            {getStatusText(task.status)}
          </span>
        </div>

        <div className="task-detail-grid">
          <div className="task-detail-sidebar">
            <h3 className="task-detail-section-title">
              <FaInfoCircle className="task-detail-section-icon" /> Task Details
            </h3>
            <div className="task-detail-section-card with-hover-effect">
              <div className="task-detail-item">
                <div className="task-detail-item-icon project">
                  <FaProjectDiagram />
                </div>
                <div className="task-detail-item-content">
                  <p className="task-detail-item-label">PROJECT</p>
                  <p className="task-detail-item-value highlight">{task.project_name}</p>
                </div>
              </div>
              
              <div className="task-detail-item">
                <div className="task-detail-item-icon assignee">
                  <FaUserAlt />
                </div>
                <div className="task-detail-item-content">
                  <p className="task-detail-item-label">ASSIGNED TO</p>
                  <p className="task-detail-item-value">{task.assignee_name}</p>
                </div>
              </div>
              
              <div className="task-detail-item">
                <div className="task-detail-item-icon assigner">
                  <FaUserCog />
                </div>
                <div className="task-detail-item-content">
                  <p className="task-detail-item-label">ASSIGNED BY</p>
                  <p className="task-detail-item-value">{task.assigner_name}</p>
                </div>
              </div>
              
              {currentUser && (currentUser.role === 'admin' || currentUser.role === 'manager') && (
                <div className="task-detail-item">
                  <div className="task-detail-item-icon expected-hours">
                    <FaClock />
                  </div>
                  <div className="task-detail-item-content">
                    <p className="task-detail-item-label">EXPECTED HOURS</p>
                    <p className="task-detail-item-value">{task.expected_hours}</p>
                  </div>
                </div>
              )}
              
              {task.actual_hours && (
                <div className="task-detail-item">
                  <div className="task-detail-item-icon actual-hours">
                    <FaClock />
                  </div>
                  <div className="task-detail-item-content">
                    <p className="task-detail-item-label">ACTUAL HOURS</p>
                    <p className="task-detail-item-value">{task.actual_hours}</p>
                  </div>
                </div>
              )}
              
              <div className="task-detail-item">
                <div className="task-detail-item-icon created">
                  <FaCalendarCheck />
                </div>
                <div className="task-detail-item-content">
                  <p className="task-detail-item-label">CREATED</p>
                  <p className="task-detail-item-value">{formatDate(task.created_at)}</p>
                </div>
              </div>
              
              {task.completed_at && (
                <div className="task-detail-item">
                  <div className="task-detail-item-icon completed">
                    <FaCheckCircle />
                  </div>
                  <div className="task-detail-item-content">
                    <p className="task-detail-item-label">COMPLETED</p>
                    <p className="task-detail-item-value">{formatDate(task.completed_at)}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="task-detail-main-content">
            <h3 className="task-detail-section-title">
              <FaClipboardList className="task-detail-section-icon" /> Description
            </h3>
            <div className="task-detail-section-card with-hover-effect description-card">
              <p className="task-description">
                {task.description || 
                  <span className="task-description-empty">No description provided.</span>
                }
              </p>
            </div>

            {task.completion_note && (
              <>
                <h3 className="task-detail-section-title completion">
                  <FaCheckCircle className="task-detail-section-icon" /> Completion Notes
                </h3>
                <div className="task-detail-section-card with-hover-effect completion-card">
                  <p className="task-description">{task.completion_note}</p>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Only show the form if the task is assigned to the current user or the user is an admin */}
        {currentUser && (currentUser.id === task.assigned_to || currentUser.role === 'admin' || currentUser.role === 'manager') && task.status !== 'completed' && (
          <div className="task-update-form-container slide-in-bottom">
            <div className="task-update-header">
              <h3 className="task-detail-section-title">
                <FaClipboardList className="task-detail-section-icon update-icon" /> Update Task Status
              </h3>
              <div className="task-update-note">Changes will be tracked in task history</div>
            </div>
            <div className="task-update-form-wrapper">
              <TaskUpdateForm task={task} onSubmitSuccess={handleStatusUpdate} />
            </div>
          </div>
        )}

        {currentUser && (currentUser.role === 'admin' || currentUser.role === 'manager') && (
          <div className="task-actions slide-in-right">
            <div className="task-actions-wrapper">
              <Link
                to={`/tasks/edit/${task.id}`}
                className="btn btn-outline-primary action-btn edit-btn"
              >
                <FaPencilAlt className="btn-icon" /> Edit Task
              </Link>
              {!showConfirmDelete ? (
                <button
                  onClick={() => setShowConfirmDelete(true)}
                  className="btn btn-outline-danger action-btn delete-btn"
                >
                  <FaTrashAlt className="btn-icon" /> Delete Task
                </button>
              ) : (
                <div className="delete-confirmation scale-in-animation">
                  <div className="delete-confirmation-box">
                    <span className="delete-confirmation-message">Are you sure you want to delete this task?</span>
                    <div className="delete-confirmation-actions">
                      <button
                        onClick={handleDelete}
                        className="btn btn-danger btn-sm"
                      >
                        <FaTrashAlt className="btn-icon" /> Yes, Delete
                      </button>
                      <button
                        onClick={() => setShowConfirmDelete(false)}
                        className="btn btn-neutral btn-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default TaskDetailPage;
